import { GetStaticPaths } from 'next';
import { useEffect, useState } from 'react';
import { GetStaticPropsContext } from 'next/types';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getLayoutData } from '@features/layout/api';
import { useResponsive } from '@features/responsive';
import { IParams } from '@features/categories';
import { TCmsBlocks, IInspirationProduct, IContentNavigation } from '@features/cms/typescript';
import { IAppearanceObject } from '@features/pages/typescript';
import MainSeo from '@components/MainSeo/MainSeo';
import { REVALIDATION_TIME } from '@config/index';
import { TLocale, WindowType, ISeo } from '@typescript/index';
import { getStaticPageCmsDocumentBySlug } from '@features/cms/services/CmsServices';
import RenderCmsBlock from '@features/cms/components/RenderCmsBlock';
import ProductsListing from '@features/cms/components/pages/ProductsListing/ProductsListing';
import SelfCanonicalLink from '@features/seo/SelfCanonicalLink/SelfCanonicalLink';
import { getDevProps } from '@components/DevToolbar/helpers/getDevProps';
import ContentNavigation from '@components/ContentNavigation/ContentNavigation';
import getNavigationVariant from '@utils/getNavigationVariant';
import { trackRevalidation } from '@utils/revalidationTracker';
import logger from '@utils/logger';
import { removeUndefined } from '@utils/removeUndefined';
type PropsType = {
  appearance: IAppearanceObject | undefined;
  seo: ISeo | undefined;
  contentNavigation: IContentNavigation[] | null | undefined;
  products: IInspirationProduct[] | undefined;
  content: Record<string, TCmsBlocks> | null;
  isBuildTime?: boolean;
};
const Terms = ({
  appearance,
  seo,
  contentNavigation,
  products,
  content: blocks,
  isBuildTime
}: PropsType) => {
  const [currentView, setCurrentView] = useState<WindowType | null>(null);
  const {
    isMobile,
    isMobilelg,
    isTablet,
    isTabletlg,
    isWiderThanDesktop,
    currentBreakpoint
  } = useResponsive();
  const withNavigation = !!(contentNavigation && contentNavigation.length);
  useEffect(() => {
    if (isMobile || isMobilelg) {
      setCurrentView(WindowType.mobile);
    } else if (isTablet || isTabletlg) {
      setCurrentView(WindowType.tablet);
    } else if (isWiderThanDesktop) {
      setCurrentView(WindowType.desktop);
    } else {
      setCurrentView(WindowType.desktop);
    }
  }, [currentBreakpoint]);
  if (isBuildTime) {
    return <div>Build time</div>;
  }
  return <>
      <SelfCanonicalLink />
      {withNavigation && <ContentNavigation links={contentNavigation} />}
      <main id="main-content" sx={{
      background: appearance?.backgroundColor ?? 'neutralWhite'
    }}>
        {seo && <MainSeo seo={seo} />}
        {blocks && Object.keys(blocks).map((key, index) => <RenderCmsBlock key={key} block={blocks[key]} isFirst={!index} defaultBackground={appearance?.backgroundColor || 'neutralWhite'} />)}
        {products && products.length > 0 && <ProductsListing products={products} windowType={currentView || WindowType.mobile} />}
      </main>
    </>;
};
export const getStaticProps = async ({
  params,
  locale
}: GetStaticPropsContext<IParams>) => {
  if (process.env.IS_BUILD) {
    return {
      props: {
        isBuildTime: true
      },
      revalidate: 1
    };
  }
  if (params === undefined) {
    return new Error('Params missing');
  }
  const {
    slug
  } = params;
  const isSlugCorrect = /^[A-z0-9-/]*$/.test(slug);
  if (!isSlugCorrect) {
    return {
      notFound: true,
      revalidate: true
    };
  }
  const document = await getStaticPageCmsDocumentBySlug(locale as TLocale, slug);
  if (!document || !document.id) return {
    notFound: true,
    revalidate: true
  };
  const content = await document.getAdaptedContent();
  const navParams = getNavigationVariant(document.appearance?.navigationVariant, content ?? {}, document.appearance?.backgroundColor ?? 'neutralWhite');
  const layoutData = await getLayoutData(locale as TLocale);
  try {
    await trackRevalidation(`pages/${slug}`, locale as string, REVALIDATION_TIME, 'static', 'pages');
  } catch (error) {
    logger.error('Failed to track revalidation:', error);
  }
  const {
    appearance,
    seo,
    contentNavigation,
    products
  } = document;
  if (document?.seo) document.seo.imagePaths = document.getAllImagePaths();
  return {
    props: {
      ...getDevProps('static', REVALIDATION_TIME),
      appearance,
      seo,
      contentNavigation,
      products,
      content: content ? removeUndefined(content) : null,
      isBuildTime: false,
      ...layoutData,
      ...(await serverSideTranslations(locale as string)),
      navParams
    },
    revalidate: REVALIDATION_TIME
  };
};
export const getStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking'
});
export default Terms;